import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments';
import PracticeService from '@/services/practice-service';
import { getSoulMapDownloadErrorMessage } from '@/utils/soulmap-document-download';
import { saveAs } from 'file-saver';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

/** Lets the backend record the download before download counts are re-read. */
const REFRESH_AFTER_DOWNLOAD_MS = 1200;

export const SOUL_MAP_PDF_FILENAME = 'soul-map-full.pdf';

/**
 * Downloads a Soul Map document through the SSO or password-session path and
 * refreshes the document status afterwards so labels reflect the download.
 */
export function useSoulMapDocumentDownload(
  practiceId: string,
  statusQuery: ReturnType<typeof useSoulMapDocuments>
) {
  const { data, refetch } = statusQuery;
  const [isDownloading, setIsDownloading] = useState(false);

  const download = useCallback(
    async (downloadUrl: string, fallbackFilename = SOUL_MAP_PDF_FILENAME) => {
      if (!downloadUrl || isDownloading) {
        return;
      }

      setIsDownloading(true);
      try {
        const result = await PracticeService.downloadSoulMapDocument(
          downloadUrl,
          fallbackFilename,
          {
            practiceId: Number(practiceId),
            documentStatus: data,
          }
        );
        if (result.mode === 'blob') {
          saveAs(result.blob, result.filename);
        }
        window.setTimeout(() => void refetch(), REFRESH_AFTER_DOWNLOAD_MS);
      } catch (error) {
        console.error('Soul Map PDF download failed:', error);
        toast.error(getSoulMapDownloadErrorMessage(error));
      } finally {
        setIsDownloading(false);
      }
    },
    [data, isDownloading, practiceId, refetch]
  );

  return { download, isDownloading };
}
